import { Fragment, useMemo, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Award,
  Briefcase,
  Cpu,
  Globe,
  GraduationCap,
  History,
  Layers,
  Lightbulb,
  Mail,
  MapPin,
  Printer,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { data } from "../lib/data";
import {
  computeYears,
  getEducation,
  getProfile,
  getQualifications,
  getTechnology,
} from "../lib/queries";
import { INDUSTRY_HUE, TECH_CAT_HUE, TECH_GROUPS, industryIcon, techGroupIcon } from "../lib/visuals";
import { Carousel, type CarouselSlide } from "../components/Carousel";
import { ErasRoad } from "../components/ErasRoad";
import { TimelinePanel } from "../components/TimelinePanel";
import { GraphPanel } from "../components/GraphPanel";
import { LensGrid, TechnologyBlocks } from "../components/LensGrids";
import { AskInline } from "../components/ChatPanel";

/* LinkedIn glyph — lucide-react dropped brand icons; path kept from the prototype's hand-rolled set. */
function LinkedinGlyph({ size = 15, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect width="4" height="12" x="2" y="9" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

/* ── Hero ─────────────────────────────────────────────────────────────────── */

const HERO_TAG_IDS: { id: string; type: "tech" | "industry"; label?: string; brk?: boolean }[] = [
  // Row 1 — Specializations
  { id: "ai-adoption", type: "tech", label: "Enterprise AI Adoption" },
  { id: "cloud-governance", type: "tech" },
  { id: "hybrid-cloud", type: "tech", label: "Hybrid Cloud Migration" },
  // Row 2 — Industries
  { id: "healthcare", type: "industry", brk: true },
  { id: "financial-services", type: "industry" },
  { id: "insurance", type: "industry" },
  { id: "telecom", type: "industry" },
  // Rest — Technologies
  { id: "amazon-connect", type: "tech", brk: true },
  { id: "ai-ml", type: "tech", label: "Artificial Intelligence" },
  { id: "claude-code", type: "tech", label: "Claude Code & Design" },
  { id: "bedrock-agentcore", type: "tech", label: "Bedrock" },
];

interface HeroTag {
  key: string;
  label: string;
  to: string;
  hue: string;
  brk?: boolean;
}

function Hero() {
  const p = getProfile();
  const tags = useMemo(() => {
    const result: HeroTag[] = [];
    for (const item of HERO_TAG_IDS) {
      if (item.type === "industry") {
        const ind = data.industries.find((i) => i.id === item.id);
        if (ind) {
          result.push({
            key: `i-${item.id}`,
            label: item.label ?? ind.label,
            to: `/by/industry?industry=${item.id}`,
            hue: INDUSTRY_HUE[item.id] || "var(--accent)",
            brk: item.brk,
          });
        }
      } else {
        const tech = getTechnology(item.id);
        if (tech) {
          result.push({
            key: `t-${item.id}`,
            label: item.label ?? tech.label,
            to: `/by/technology?tech=${item.id}`,
            hue: (tech.category && TECH_CAT_HUE[tech.category]) || "var(--accent)",
            brk: item.brk,
          });
        }
      }
    }
    return result;
  }, []);
  return (
    <header className="hero wrap">
      <div className="hero-grid">
        <div className="hero-main" style={{ width: "675px" }}>
          <p className="eyebrow rise">Interactive résumé</p>
          <h1 className="display hero-name rise" style={{ animationDelay: "40ms", fontSize: "64px" }}>
            {p.name}
          </h1>
          <div className="hero-intro rise" style={{ animationDelay: "100ms" }}>
            <div className="hero-photo-wrap">
              <img className="hero-photo" src="/headshot.jpg" alt={p.name} loading="eager" />
              <span className="hero-now mono">Enterprise Architect</span>
            </div>
            <div className="hero-intro-body">
              <p className="hero-headline">{p.headline}</p>
              <div className="hero-meta">
                <span className="row" style={{ gap: 6 }}>
                  <MapPin size={15} className="muted" />
                  {p.location}
                </span>
                <a className="row hero-meta-link" style={{ gap: 6 }} href={`mailto:${p.email}`}>
                  <Mail size={15} className="muted" />
                  {p.email}
                </a>
                {p.linkedin && (
                  <a
                    className="row hero-meta-link"
                    style={{ gap: 6 }}
                    href={p.linkedin}
                    target="_blank"
                    rel="noopener"
                  >
                    <LinkedinGlyph size={15} className="muted" />
                    LinkedIn
                  </a>
                )}
                <Link className="row hero-meta-link" style={{ gap: 6 }} to="/print">
                  <Printer size={15} className="muted" />
                  Print / PDF
                </Link>
              </div>
            </div>
          </div>
          <div className="hero-tags-block">
            <p className="hero-tags-head mono">Most viewed</p>
            <div className="hero-tags">
              {tags.map((t) => (
                <Fragment key={t.key}>
                  {t.brk && <span className="hero-tag-break" aria-hidden="true" />}
                  <Link to={t.to} className="hero-tag" style={{ "--tag-hue": t.hue } as CSSProperties}>
                    <span className="hero-tag-dot" />
                    {t.label}
                  </Link>
                </Fragment>
              ))}
            </div>
          </div>
        </div>
        <div className="hero-side fade" style={{ animationDelay: "200ms" }}>
          <AskInline />
          <HeroMetrics />
        </div>
      </div>
    </header>
  );
}

function HeroMetrics() {
  const metrics: { v: string; l: string; to: string; Icon: LucideIcon }[] = [
    { v: `${computeYears()}+`, l: "Years", to: "/?cview=timeline", Icon: History },
    { v: String(data.roles.length), l: "Roles", to: "/by/role", Icon: Briefcase },
    { v: String(data.industries.length), l: "Industries", to: "/by/industry", Icon: Globe },
    { v: String(data.technologies.length), l: "Technologies", to: "/by/technology", Icon: Cpu },
    { v: String(data.skills.length), l: "Skills", to: "/by/skill", Icon: Lightbulb },
    { v: `${data.projects.length}+`, l: "Engagements", to: "/?cview=graph", Icon: Layers },
  ];
  return (
    <div className="hero-metrics fade" style={{ animationDelay: "280ms" }}>
      {metrics.map((m) => (
        <Link className="hmetric" key={m.l} to={m.to} aria-label={`${m.v} ${m.l}`}>
          <div className="hmetric-stack">
            <m.Icon className="hmetric-watermark" aria-hidden="true" />
            <div className="hmetric-v">{m.v}</div>
          </div>
          <div className="hmetric-l">{m.l}</div>
        </Link>
      ))}
    </div>
  );
}

/* ── Profile section (#profile) ───────────────────────────────────────────── */

function ctaMeta(href: string): { Icon: LucideIcon; hue: string } {
  const ind = href.match(/industry=([\w-]+)/);
  if (ind) return { Icon: industryIcon(ind[1]), hue: INDUSTRY_HUE[ind[1]] || "#6f76e0" };
  const tech = href.match(/tech=([\w-]+)/);
  if (tech) {
    const cat = getTechnology(tech[1])?.category;
    const group = cat ? TECH_GROUPS.find((g) => g.cats.includes(cat)) : undefined;
    return {
      Icon: group ? techGroupIcon(group.key) : Sparkles,
      hue: (cat && TECH_CAT_HUE[cat]) || "#6f76e0",
    };
  }
  return { Icon: ArrowUpRight, hue: "#6f76e0" };
}

function ProfilePanel() {
  const p = getProfile();
  return (
    <div className="profile-solo">
      <div className="profile-cols">
        <div className="profile-textcol">
          <h3 className="section-tag eyebrow">Profile</h3>
          <div className="profile-body">
            {p.summary.split(/\n\s*\n+/).map((para, i) => (
              <p key={i} className={i === 0 ? "profile-lead" : ""}>
                {para.trim()}
              </p>
            ))}
          </div>
        </div>
        <JumpIn />
      </div>
    </div>
  );
}

function JumpIn() {
  const p = getProfile();
  return (
    <div className="profile-ctas jump-in">
      <h3 className="eyebrow">Featured views & lenses</h3>
      {(p.ctas ?? []).map((c) => {
        const meta = ctaMeta(c.href);
        return (
          <Link key={c.label} to={c.href} className="cta-card card card-link">
            <span
              className="cta-ico"
              style={{
                color: meta.hue,
                background: `color-mix(in oklab, ${meta.hue} 13%, var(--card))`,
                borderColor: `color-mix(in oklab, ${meta.hue} 30%, var(--rule))`,
              }}
            >
              <meta.Icon size={17} />
            </span>
            <div className="cta-body">
              <div className="cta-label">{c.label}</div>
              <div className="cta-desc">{c.description}</div>
            </div>
            <ArrowUpRight size={16} className="cta-arrow" />
          </Link>
        );
      })}
    </div>
  );
}

function ProfileSection() {
  return (
    <section className="section wrap" id="profile">
      <ProfilePanel />
    </section>
  );
}

/* ── Career & Lens view sections (#career-views, #lens-views) ─────────────── */

const CV_SLIDES: CarouselSlide[] = [
  { key: "eras", label: "Eras", hue: "#6f76e0", render: () => <ErasRoad /> },
  { key: "timeline", label: "Timeline", hue: "#4f9fd6", render: () => <TimelinePanel /> },
  { key: "graph", label: "Graph", hue: "#c577cf", render: () => <GraphPanel /> },
];

type LensSlideKey = "industry" | "skill" | "role" | "technology";

const LENS_SUB: Record<LensSlideKey, string> = {
  industry: "Cross-sector practice across regulated and commercial domains.",
  skill: "Capabilities applied across three decades of delivery.",
  role: "The arc from systems analyst to chief architect.",
  technology: "Platforms, languages, and AI tooling — newest stack on top.",
};

function LensSlide({ lens }: { lens: LensSlideKey }) {
  return (
    <div className="lens-slide">
      <p className="section-sub" style={{ marginTop: 0, marginBottom: 18 }}>
        {LENS_SUB[lens]}
      </p>
      {lens === "technology" ? <TechnologyBlocks /> : <LensGrid lens={lens} />}
    </div>
  );
}

const LV_SLIDES: CarouselSlide[] = [
  { key: "industry", label: "Industry", hue: "#3fa89b", render: () => <LensSlide lens="industry" /> },
  { key: "skill", label: "Skill", hue: "#6f76e0", render: () => <LensSlide lens="skill" /> },
  { key: "role", label: "Role", hue: "#dd9a4e", render: () => <LensSlide lens="role" /> },
  { key: "technology", label: "Technology", hue: "#c577cf", render: () => <LensSlide lens="technology" /> },
];

const VIEW_SECTIONS = {
  career: {
    id: "career-views",
    eyebrow: "Career",
    title: "A rose by any other name",
    sub: "Our stories are often narrated as journeys, milestones, and relationships. Three views to suit the moment.",
    slides: CV_SLIDES,
    param: "cview" as const,
    fallback: "eras",
  },
  lens: {
    id: "lens-views",
    eyebrow: "Lens",
    title: "Eyes of the beholder",
    sub: "What matters most depends on the question at hand. Four lenses to see the full spectrum of experience.",
    slides: LV_SLIDES,
    param: "lview" as const,
    fallback: "industry",
  },
};

function ViewsSection({ which }: { which: keyof typeof VIEW_SECTIONS }) {
  const cfg = VIEW_SECTIONS[which];
  return (
    <section className="section wrap views-section" id={cfg.id}>
      <div className="views-head">
        <p className="eyebrow">{cfg.eyebrow}</p>
        <h2 className="display views-title">{cfg.title}</h2>
        <p
          className="section-sub"
          style={{
            height: "25px",
            width: "100%",
            lineHeight: "1.6",
            fontSize: "16px",
            fontFamily: '"Hanken Grotesk"',
          }}
        >
          {cfg.sub}
        </p>
      </div>
      <Carousel param={cfg.param} slides={cfg.slides} defaultKey={cfg.fallback} />
    </section>
  );
}

/* ── Credentials (#credentials) ───────────────────────────────────────────── */

const FAVICON = (domain: string, size = 128) =>
  `https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://${domain}&size=${size}`;

const EDU_DOMAIN: Record<string, string> = { "mba-unc": "unc.edu", "bs-etce": "unom.ac.in" };
const ISSUER_DOMAIN: Record<string, string> = {
  PMI: "pmi.org",
  "Sun Microsystems": "oracle.com",
  Lotus: "ibm.com",
  "University of North Carolina at Chapel Hill": "unc.edu",
  "University of Madras, Chennai, India": "unom.ac.in",
};

function FaviconMark({
  domain,
  size = 40,
  radius = 10,
  Icon = Award,
  title,
}: {
  domain?: string;
  size?: number;
  radius?: number;
  Icon?: LucideIcon;
  title?: string;
}) {
  const [ok, setOk] = useState(Boolean(domain));
  if (domain && ok) {
    return (
      <span className="fav-mark" style={{ width: size, height: size, borderRadius: radius }} title={title}>
        <img src={FAVICON(domain, 128)} alt={title ?? ""} onError={() => setOk(false)} loading="lazy" />
      </span>
    );
  }
  return (
    <span
      className="fav-mark generic"
      style={{ width: size, height: size, borderRadius: radius }}
      title={title}
    >
      <Icon size={Math.round(size * 0.5)} />
    </span>
  );
}

function Credentials() {
  const edu = getEducation();
  const certs = getQualifications().filter((q) => q.id !== "mba-unc" && q.id !== "bs-etce");
  return (
    <section className="section wrap" id="credentials">
      <div className="edu-grid">
        <div>
          <h3 className="eyebrow">
            <GraduationCap size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />
            Education
          </h3>
          <ul className="edu-list">
            {edu.map((e) => (
              <li key={e.id}>
                <FaviconMark
                  domain={EDU_DOMAIN[e.id]}
                  size={52}
                  radius={13}
                  Icon={GraduationCap}
                  title={e.institution}
                />
                <div className="edu-body">
                  <div className="edu-degree">{e.degree}</div>
                  <div className="edu-inst">{e.institution}</div>
                  <div className="edu-years mono">
                    {e.start} – {e.end}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="eyebrow">
            <Award size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />
            Certifications
          </h3>
          <ul className="cert-list">
            {certs.map((q) => (
              <li key={q.id}>
                <FaviconMark
                  domain={q.issuer ? ISSUER_DOMAIN[q.issuer] : undefined}
                  size={48}
                  radius={12}
                  Icon={Award}
                  title={q.issuer}
                />
                <div>
                  <div className="cert-label">{q.label}</div>
                  <div className="cert-issuer mono">
                    {q.issuer}
                    {q.year ? ` · ${q.year}` : ""}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ── Landing assembly ─────────────────────────────────────────────────────── */

export function Landing() {
  return (
    <div>
      <Hero />
      <ProfileSection />
      <ViewsSection which="career" />
      <ViewsSection which="lens" />
      <Credentials />
    </div>
  );
}
